/**
 * @file camera-scan.ts
 * @description Правила скана камерой в поле склада (phase19.22 шаг 5) —
 *   чистые функции без React и без браузера, проверяются тестом.
 *
 * Что здесь решается:
 *   - показывать ли кнопку камеры: на телефоне — да, на десктопе с
 *     USB-сканером — нет (кнопка там только мешает);
 *   - каким декодером читать: встроенный `BarcodeDetector` быстрее и не
 *     тянет библиотеку, но есть не везде и не везде умеет DataMatrix —
 *     тогда `@zxing/browser`;
 *   - как превратить распознанный текст в код: GS (0x1D) у GS1 DataMatrix и
 *     пробелы внутри кода не трогаем — роутер скана сервера их ждёт;
 *   - какой текст показать, если камера не открылась.
 */

/** Режим кнопки камеры у поля: `auto` — только на сенсорных устройствах. */
export type CameraMode = boolean | "auto";

/** Что известно об устройстве. */
export interface CameraEnv {
  /** Есть `navigator.mediaDevices.getUserMedia` (только HTTPS/localhost). */
  hasGetUserMedia: boolean;
  /** Основной указатель — палец (`matchMedia("(pointer: coarse)")`). */
  coarsePointer: boolean;
  /** `false` — камер нет; `null` — ещё не знаем (до `enumerateDevices`). */
  hasVideoInput: boolean | null;
}

/** Показывать ли кнопку «Сканировать камерой». */
export function shouldShowCameraButton(mode: CameraMode, env: CameraEnv): boolean {
  if (mode === false) return false;
  if (!env.hasGetUserMedia || env.hasVideoInput === false) return false;
  // `true` — вызывающий явно просит камеру (например, планшет с мышью).
  return mode === true || env.coarsePointer;
}

/** Символики склада: DataMatrix — основная, Code128 — под USB, плюс QR и EAN. */
export const CAMERA_FORMATS = ["data_matrix", "code_128", "qr_code", "ean_13", "ean_8"] as const;

/** Каким декодером читаем кадры. */
export type DecoderKind = "native" | "zxing";

/**
 * Выбор декодера. Встроенный — только если он есть и умеет DataMatrix (наш
 * основной код): Chrome на Windows/Linux объявляет `BarcodeDetector`, но без
 * DataMatrix, а `null` (список форматов не получили) — повод не рисковать.
 */
export function pickDecoderKind(env: {
  hasBarcodeDetector: boolean;
  supportedFormats: readonly string[] | null;
}): DecoderKind {
  if (!env.hasBarcodeDetector || !env.supportedFormats) return "zxing";
  return env.supportedFormats.includes("data_matrix") ? "native" : "zxing";
}

/** Форматы для встроенного детектора: только те, что он поддерживает. */
export function nativeFormats(supported: readonly string[]): BarcodeDetectorFormat[] {
  return CAMERA_FORMATS.filter((f) => supported.includes(f));
}

/**
 * Распознанный текст → код для `onSubmit`. Срезаем только перевод строки и
 * NUL по краям (некоторые декодеры добавляют их к QR); GS (0x1D) и пробелы
 * — часть кода. Пустой результат — `null`: такой «скан» не отправляем.
 */
export function normalizeScannedCode(raw: string): string | null {
  const code = raw.replace(/^[\r\n\0]+|[\r\n\0]+$/g, "");
  return code.trim() === "" ? null : code;
}

/** Ошибка открытия камеры языком кладовщика. */
export interface CameraErrorInfo {
  text: string;
  /** true — камеры на устройстве нет: кнопку больше не показываем. */
  noCamera: boolean;
}

/** Имя `DOMException` → текст. Разрешение — главный случай на телефоне. */
export function cameraErrorText(err: unknown): CameraErrorInfo {
  const name = err instanceof Error || err instanceof DOMException ? err.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return { text: "Разрешите доступ к камере в настройках браузера", noCamera: false };
    case "NotFoundError":
    case "OverconstrainedError":
      return { text: "Камера не найдена на этом устройстве", noCamera: true };
    case "NotReadableError":
    case "AbortError":
      return { text: "Камера занята другим приложением — закройте его и повторите", noCamera: false };
    default:
      return { text: "Не удалось включить камеру — введите код вручную", noCamera: false };
  }
}
